import { create } from 'zustand';

export const useMeetingStore = create((set, get) => ({
  // Meeting info
  meeting: null,
  participant: null,
  isHost: false,
  status: 'idle', // idle, waiting, joining, joined, ended

  // Participants
  participants: [],
  waitingList: [],

  // Media state
  audioEnabled: true,
  videoEnabled: true,
  screenSharing: false,
  handRaised: false,

  // Translation
  translationEnabled: false,
  sourceLanguage: 'en',
  targetLanguage: 'en',
  translatingPeers: new Set(), // Peers sending us translated audio (need video delay)

  // Chat
  messages: [],
  unreadCount: 0,
  chatOpen: false,

  // Transcripts/subtitles
  transcripts: [],
  localTranscripts: [], // Local STT transcriptions (for subtitles without translation)
  subtitlesEnabled: false,
  sttActive: false, // Whether local STT is running
  sttLoading: false, // Whether STT model is loading
  sttLoadingProgress: 0, // Model loading progress (0-100)
  activeSpeaker: null, // peerId of currently speaking participant
  activeSpeakerTimeout: null, // Timeout to clear active speaker

  // UI state
  settingsOpen: false,
  participantsOpen: false,
  layout: 'grid', // grid, speaker, sidebar

  // Actions
  setMeeting: (meeting) => set({ meeting }),
  setParticipant: (participant) => set({ participant }),
  setIsHost: (isHost) => set({ isHost }),
  setStatus: (status) => set({ status }),

  // Participants actions
  setParticipants: (participants) => set({ participants }),
  addParticipant: (participant) =>
    set((state) => {
      // Prevent duplicates by checking socketId
      if (state.participants.some((p) => p.socketId === participant.socketId)) {
        console.warn('[MeetingStore] Duplicate participant ignored:', participant.socketId);
        return state;
      }
      return { participants: [...state.participants, participant] };
    }),
  removeParticipant: (socketId) =>
    set((state) => ({
      participants: state.participants.filter((p) => p.socketId !== socketId),
    })),
  updateParticipant: (socketId, updates) =>
    set((state) => ({
      participants: state.participants.map((p) =>
        p.socketId === socketId ? { ...p, ...updates } : p
      ),
    })),

  // Waiting room actions
  setWaitingList: (waitingList) => set({ waitingList }),

  // Media actions
  setAudioEnabled: (enabled) => set({ audioEnabled: enabled }),
  setVideoEnabled: (enabled) => set({ videoEnabled: enabled }),
  setScreenSharing: (sharing) => set({ screenSharing: sharing }),
  setHandRaised: (raised) => set({ handRaised: raised }),

  // Translation actions
  setTranslationEnabled: (enabled) => set({ translationEnabled: enabled }),
  setSourceLanguage: (lang) => set({ sourceLanguage: lang }),
  setTargetLanguage: (lang) => set({ targetLanguage: lang }),
  addTranslatingPeer: (peerId) =>
    set((state) => {
      const newSet = new Set(state.translatingPeers);
      newSet.add(peerId);
      return { translatingPeers: newSet };
    }),
  removeTranslatingPeer: (peerId) =>
    set((state) => {
      const newSet = new Set(state.translatingPeers);
      newSet.delete(peerId);
      return { translatingPeers: newSet };
    }),
  clearTranslatingPeers: () => set({ translatingPeers: new Set() }),

  // Chat actions
  addMessage: (message) =>
    set((state) => ({
      messages: [...state.messages, message],
      unreadCount: state.chatOpen ? 0 : state.unreadCount + 1,
    })),
  setMessages: (messages) => set({ messages }),
  setChatOpen: (open) => set({ chatOpen: open, unreadCount: open ? 0 : get().unreadCount }),

  // Transcript actions - utterance-based grouping for WhatsApp-style display
  // Each utterance = { id, peerId, serverUtteranceId, original, translation, isComplete, timestamp }
  // serverUtteranceId: unique ID from server to match translations to correct utterances
  // isComplete: translation finished - utterance fully done
  addTranscript: (transcript) => {
    const { peerId, text, type, utteranceId } = transcript;
    const state = get();

    // Create a unique key combining peerId + server utteranceId
    const serverUtteranceId = utteranceId !== undefined ? `${peerId}-${utteranceId}` : null;

    // Update active speaker (clear after 2s of no activity)
    if (state.activeSpeakerTimeout) {
      clearTimeout(state.activeSpeakerTimeout);
    }
    const timeout = setTimeout(() => {
      set({ activeSpeaker: null, activeSpeakerTimeout: null });
    }, 2000);

    set((state) => {
      let utterances = [...state.transcripts];

      if (type === 'original' || type === 'original_final') {
        // Find or create utterance by serverUtteranceId
        const existingIndex = serverUtteranceId
          ? utterances.findIndex((u) => u.serverUtteranceId === serverUtteranceId)
          : -1;

        if (existingIndex >= 0) {
          // Update existing utterance's original
          utterances[existingIndex] = {
            ...utterances[existingIndex],
            original: text,
            timestamp: Date.now(),
          };
        } else {
          // Create new utterance with serverUtteranceId
          utterances.push({
            id: `${peerId}-${Date.now()}`,
            peerId,
            serverUtteranceId,
            original: text,
            translation: '',
            isComplete: false,
            timestamp: Date.now(),
          });
        }
      } else if (type === 'translated') {
        // FINAL translation - match by serverUtteranceId
        const targetIndex = serverUtteranceId
          ? utterances.findIndex((u) => u.serverUtteranceId === serverUtteranceId)
          : -1;

        if (targetIndex >= 0) {
          utterances[targetIndex] = {
            ...utterances[targetIndex],
            translation: text,
            isComplete: true, // Final translation = fully complete
          };
        } else {
          console.warn(`[Store] Translation received but no matching utterance found for ${serverUtteranceId}`);
        }
      }
      // Note: 'translated_partial' is intentionally ignored - we only show final translations
      // This avoids showing incomplete/wrong partial translations

      // Keep last 20 utterances
      if (utterances.length > 20) {
        utterances = utterances.slice(-20);
      }

      return {
        transcripts: utterances,
        activeSpeaker: peerId,
        activeSpeakerTimeout: timeout,
      };
    });
  },
  setSubtitlesEnabled: (enabled) => set({ subtitlesEnabled: enabled }),

  // Local STT actions
  setSTTActive: (active) => set({ sttActive: active }),
  setSTTLoading: (loading) => set({ sttLoading: loading }),
  setSTTLoadingProgress: (progress) => set({ sttLoadingProgress: progress }),

  // Add local transcription (from browser-based STT)
  addLocalTranscript: (transcript) => {
    const { peerId, text, utteranceId } = transcript;
    const state = get();

    set((state) => {
      let transcripts = [...state.localTranscripts];

      // Add new transcript
      transcripts.push({
        id: `local-${peerId}-${utteranceId}-${Date.now()}`,
        peerId,
        text,
        utteranceId,
        timestamp: Date.now(),
        isLocal: true, // Marked as local STT (not cloud)
      });

      // Keep last 50 local transcripts
      if (transcripts.length > 50) {
        transcripts = transcripts.slice(-50);
      }

      return { localTranscripts: transcripts };
    });

    // Update active speaker
    state.setActiveSpeaker(peerId);
  },

  // Clear all local transcripts
  clearLocalTranscripts: () => set({ localTranscripts: [] }),

  // Active speaker (from VAD or transcripts)
  setActiveSpeaker: (peerId) => {
    const state = get();

    // Clear existing timeout
    if (state.activeSpeakerTimeout) {
      clearTimeout(state.activeSpeakerTimeout);
    }

    if (peerId) {
      // Set active speaker with auto-clear timeout (2s after last update)
      const timeout = setTimeout(() => {
        set({ activeSpeaker: null, activeSpeakerTimeout: null });
      }, 2000);
      set({ activeSpeaker: peerId, activeSpeakerTimeout: timeout });
    } else {
      // Clear active speaker immediately
      set({ activeSpeaker: null, activeSpeakerTimeout: null });
    }
  },

  // UI actions
  setSettingsOpen: (open) => set({ settingsOpen: open }),
  setParticipantsOpen: (open) => set({ participantsOpen: open }),
  setLayout: (layout) => set({ layout }),

  // Reset
  reset: () =>
    set({
      meeting: null,
      participant: null,
      isHost: false,
      status: 'idle',
      participants: [],
      waitingList: [],
      audioEnabled: true,
      videoEnabled: true,
      screenSharing: false,
      handRaised: false,
      translationEnabled: false,
      sourceLanguage: 'en',
      targetLanguage: 'en',
      translatingPeers: new Set(),
      messages: [],
      unreadCount: 0,
      chatOpen: false,
      transcripts: [],
      localTranscripts: [],
      subtitlesEnabled: false,
      sttActive: false,
      sttLoading: false,
      sttLoadingProgress: 0,
      activeSpeaker: null,
      activeSpeakerTimeout: null,
      settingsOpen: false,
      participantsOpen: false,
      layout: 'grid',
    }),
}));
